import { PropTypes } from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import Modal from '../../../../../components/Modal';
import RemoveModal from '../../../../../components/RemoveModal';
import { MainContext } from '../../../../../context/MainContext';
import WithBackButton from '../../../../../layout/WithBackButton';
import {
  clearCreated,
  clearEdited,
  clearErrors,
  createMixture,
  deleteMixtures,
  getMixtures,
  updateMixture,
  finalizeMixture,
} from '../../../../../services/redux/thermalControl/mixtures/actions';
import CreateMixture from '../Create';
import { COLUMNS_HEADERS } from './columns';
import styles from './index.module.scss';
import moment from 'moment';
import { NotificationsContext } from '../../../../../context/NotificationsContext';
import ViewMixture from '../View';
import BatteryStatus from '../../../../../components/BatteryStatus';

const { actionsWrapper } = styles;

const List = ({ getItems, deleteItems }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { isCreated, isEdited, errorMessages } = useSelector(
    ({ thermalControlMixtures }) => thermalControlMixtures,
  );
  const [initialFilters, setInitialFilters] = useState({ sortable: 'id-DESC' });
  const [tableKey, setTableKey] = useState(0);
  const currentFilters = useRef({ sortable: 'id-DESC' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [mixtureForEdit, setMixtureForEdit] = useState();
  const [showViewModal, setShowViewModal] = useState(false);
  const [mixtureForView, setMixtureForView] = useState();

  const handleTableChange = useCallback(({ filters }) => {
    currentFilters.current = filters;
    if (filters?.sortable?.endsWith('-NONE')) {
      setInitialFilters({ sortable: 'id-DESC' });
    }
  }, []);

  const getItemsWithDefaultSort = useCallback(
    ({ size, page, filters }) => {
      const resolvedFilters = filters?.sortable?.endsWith('-NONE')
        ? { ...filters, sortable: 'id-DESC' }
        : filters;
      getItems({ size, page, filters: resolvedFilters });
    },
    [getItems],
  );

  const onSingleDeleteButtonClick = (mixture) => {
    setSelectedRows([mixture]);
    setShowRemoveModal(true);
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    deleteItems(selectedRows);
    setSelectedRows([]);
  };

  const handleCreateMixture = (mixture) => {
    setLoading(true);
    dispatch(
      createMixture({ ...mixture, parameters: mixture.parameters || [] }),
    );
  };

  const editMixture = (mixture) => {
    setLoading(true);
    dispatch(
      updateMixture({ ...mixture, parameters: mixture.parameters || [] }),
    );
  };

  const finishMixture = (mixture) => {
    setLoading(true);
    dispatch(
      finalizeMixture({ ...mixture, parameters: mixture.parameters || [] }),
    );
  };

  useEffect(() => {
    if (isEdited) {
      setLoading(false);
      setMixtureForEdit(undefined);
      setShowEditModal(false);
      dispatch(clearEdited());
    }
  }, [isEdited, setLoading, dispatch]);

  useEffect(() => {
    if (isCreated) {
      setLoading(false);
      setShowCreateModal(false);
      dispatch(clearCreated());
      setInitialFilters({ ...currentFilters.current });
      setTableKey((prev) => prev + 1);
    }
  }, [isCreated, setLoading, dispatch]);

  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch, showCreateModal, showEditModal]);

  useEffect(() => {
    if (errorMessages) {
      const { status } = errorMessages.response;
      pushNotification({
        message: intl.formatMessage({
          id: `${
            status === 403
              ? 'strength.thermalcontrol.mixtures.delete.403.error'
              : 'messages.default'
          }`,
        }),
        type: 'error',
      });
    }
  }, [errorMessages, intl, pushNotification]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <AccionaButton
            id="createBtn"
            onClick={() => {
              setShowCreateModal(true);
            }}
            msg={intl.formatMessage({ id: 'button.add' })}
            classIcon="icon fas fa-plus"
            type="white"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            key={tableKey}
            columns={COLUMNS_HEADERS}
            getItems={getItemsWithDefaultSort}
            entity="thermalControlMixtures"
            initialFilters={initialFilters}
            onChange={handleTableChange}
            dataParseFunction={(data) =>
              data.map((d) => ({
                ...d,
                device:
                  d.device === null ? (
                    intl.formatMessage({ id: 'device.manual' })
                  ) : (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'pre',
                      }}
                    >
                      {d.device_data?.reference_number}
                      {'  '}
                      {d.device_data?.batt !== undefined ? (
                        <BatteryStatus battery={d.device_data?.batt} />
                      ) : (
                        ''
                      )}
                    </span>
                  ),
                start_time:
                  d.device === null
                    ? '---'
                    : moment(d.start_time).format('DD/MM/YYYY HH:mm'),
                finished: d.finished
                  ? intl.formatMessage({ id: 'yes' })
                  : intl.formatMessage({ id: 'no' }),
                rawObject: d,
              }))
            }
            actions={(mixture) => (
              <>
                {(mixture.rawObject.device !== null ||
                  mixture.rawObject.finished) && (
                  <AccionaTableButton
                    onClick={() => {
                      setShowViewModal(true);
                      setMixtureForView(mixture.rawObject.id);
                    }}
                    type="view"
                    toolTip={intl.formatMessage({
                      id: 'viewAction.mixtures',
                    })}
                  />
                )}
                {!mixture.rawObject.finished && (
                  <>
                    <AccionaTableButton
                      onClick={() => {
                        setShowEditModal(true);
                        setMixtureForEdit(mixture.rawObject);
                      }}
                      type="edit"
                      toolTip={intl.formatMessage({
                        id: 'editAction.mixtures',
                      })}
                    />

                    <AccionaTableButton
                      onClick={() => {
                        onSingleDeleteButtonClick(mixture.rawObject);
                      }}
                      type="delete"
                      toolTip={intl.formatMessage({
                        id: 'deleteAction.mixtures',
                      })}
                    />
                  </>
                )}
              </>
            )}
          />
        </div>
      </div>

      <RemoveModal
        entity="mixtures"
        selectedRows={selectedRows}
        msg={intl.formatMessage({ id: 'delete.mixtures' }, { total: 1 })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      {mixtureForEdit && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.thermalcontrol.mixtures.list.modal.edit.title',
            })
            .toUpperCase()}
          open={showEditModal}
          onCancel={() => {
            setShowEditModal(false);
          }}
          closable
          width="50vw"
        >
          <CreateMixture
            initialData={mixtureForEdit}
            onFinish={editMixture}
            onFinishComplete={finishMixture}
            onCancel={() => {
              setShowEditModal(false);
              setMixtureForEdit(undefined);
            }}
          />
        </Modal>
      )}
      {mixtureForView && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.thermalcontrol.mixtures.list.modal.view.title',
            })
            .toUpperCase()}
          open={showViewModal}
          onCancel={() => {
            setShowViewModal(false);
          }}
          closable
          width="70vw"
        >
          <ViewMixture
            mixtureId={mixtureForView}
            onCancel={() => {
              setShowViewModal(false);
              setMixtureForView(undefined);
            }}
          />
        </Modal>
      )}
      <Modal
        title={intl
          .formatMessage({
            id: 'strength.thermalcontrol.mixtures.list.modal.create.title',
          })
          .toUpperCase()}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false);
        }}
        closable
        width="50vw"
      >
        <CreateMixture
          onFinish={handleCreateMixture}
          onFinishComplete={finishMixture}
          onCancel={() => {
            setShowCreateModal(false);
          }}
        />
      </Modal>
    </>
  );
};

List.propTypes = {
  getItems: PropTypes.func.isRequired,
  deleteItems: PropTypes.func.isRequired,
};

export default connect(() => ({}), {
  getItems: getMixtures,
  deleteItems: deleteMixtures,
})(WithBackButton(List));
